import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import {
    formatExtendedFieldsSince,
    formatLoyaltyDateTime,
    formatLoyaltyMoney,
    formatLoyaltyNumber,
    getLoyaltyAnalyticsErrorMessage,
    isLoyaltyAnalyticsPeriodTooLong,
    MAX_PERIOD_DAYS,
    resetFilters,
    selectLoyaltyAccrualsAppliedFiltersCount,
    selectLoyaltyAnalyticsFilters,
    setIncludeReturns,
    setIncludeUnassignedStore,
    setIncludeUnknownLevel,
    setLevels,
    setPage,
    setPeriod,
    setStoreIds,
} from "@/features/loyalty-analytics"
import {
    getLoyaltyAccruals,
    getLoyaltyLevelBreakdown,
    getLoyaltyStores,
    getLoyaltySummary,
    ILoyaltyAccrualRow,
    ILoyaltyAccrualsResponse,
    ILoyaltyLevelBreakdownResponse,
    ILoyaltyStoresResponse,
    ILoyaltySummaryResponse,
} from "@/shared/api/loyalty-analytics"
import { selectStores as selectPartnerStores } from "@/entities/partner"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import notify from "@/shared/lib/notify"
import ClipboardTextSvg from "@/shared/assets/images/ClipboardText.svg"
import { ActionButton } from "@/shared/ui/action-button"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Pagination } from "@/shared/ui/pagination"
import { PeriodPicker } from "@/shared/ui/period-picker"
import { Select } from "@/shared/ui/select"
import { Table } from "@/shared/ui/table"
import { PageHeader } from "@/widgets/page-header"
import { Link } from "react-router-dom"

const PAGE_SIZE = 20
const ALL_OPTION_VALUE = "__all__"
const UNASSIGNED_OPTION_VALUE = "__unassigned__"
const UNKNOWN_LEVEL_OPTION_VALUE = "__unknown_level__"
const UNKNOWN_STORE_LABEL = "Название точки недоступно"
type LoadStatus = "idle" | "loading" | "loaded" | "error"

const getStoreName = (storeId: string, ...candidates: Array<string | null | undefined>) => (
    candidates
        .map((candidate) => candidate?.trim())
        .find((candidate) => candidate && candidate !== storeId)
)

// В выпадающем списке одинаковые подписи неразличимы, поэтому безымянные точки
// дополняются идентификатором.
const getStoreOptionLabel = (storeId: string, storeName?: string) => (
    storeName || `${UNKNOWN_STORE_LABEL} · ${storeId}`
)

const RetryState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="info_panel flex items-center justify-between gap-4">
        <span className="text-color-danger">{message}</span>
        <button type="button" onClick={onRetry} className="form_handle_button !w-auto px-6">Повторить</button>
    </div>
)

export const LoyaltyAccrualsPage = () => {
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectLoyaltyAnalyticsFilters)
    const partnerStores = useAppSelector(selectPartnerStores)
    const appliedFiltersCount = useAppSelector(selectLoyaltyAccrualsAppliedFiltersCount)
    const isPeriodTooLong = isLoyaltyAnalyticsPeriodTooLong(filters.period)
    const rowsRequestId = useRef(0)
    const storesRequestId = useRef(0)
    const levelsRequestId = useRef(0)
    const boundaryRequestId = useRef(0)
    const [rowsStatus, setRowsStatus] = useState<LoadStatus>("idle")
    const [accruals, setAccruals] = useState<ILoyaltyAccrualsResponse | null>(null)
    const [rowsError, setRowsError] = useState("")
    const [storesStatus, setStoresStatus] = useState<LoadStatus>("idle")
    const [stores, setStores] = useState<ILoyaltyStoresResponse | null>(null)
    const [storesError, setStoresError] = useState("")
    const [levelsStatus, setLevelsStatus] = useState<LoadStatus>("idle")
    const [levelBreakdown, setLevelBreakdown] = useState<ILoyaltyLevelBreakdownResponse | null>(null)
    const [levelsError, setLevelsError] = useState("")
    const [boundaryStatus, setBoundaryStatus] = useState<LoadStatus>("idle")
    const [boundarySummary, setBoundarySummary] = useState<ILoyaltySummaryResponse | null>(null)
    const [boundaryError, setBoundaryError] = useState("")

    const commonQuery = useMemo(() => ({
        from: filters.period.from,
        to: filters.period.to,
        storeIds: filters.storeIds,
        includeUnassignedStore: filters.includeUnassignedStore,
        levels: filters.levels,
        includeUnknownLevel: filters.includeUnknownLevel,
        includeReturns: filters.includeReturns,
    }), [
        filters.includeReturns,
        filters.includeUnassignedStore,
        filters.includeUnknownLevel,
        filters.levels,
        filters.period.from,
        filters.period.to,
        filters.storeIds,
    ])

    const fetchRows = useCallback(() => {
        const requestId = ++rowsRequestId.current
        if (isPeriodTooLong) {
            setRowsStatus("idle")
            setAccruals(null)
            setRowsError("")
            return
        }
        setRowsStatus("loading")
        setAccruals(null)
        setRowsError("")

        getLoyaltyAccruals({ ...commonQuery, page: filters.page, pageSize: PAGE_SIZE })
            .then((response) => {
                if (rowsRequestId.current !== requestId) return
                setAccruals(response.data)
                setRowsStatus("loaded")
            })
            .catch((error) => {
                if (rowsRequestId.current !== requestId) return
                setRowsStatus("error")
                setRowsError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [commonQuery, filters.page, isPeriodTooLong])

    const fetchStores = useCallback(() => {
        const requestId = ++storesRequestId.current
        if (isPeriodTooLong) {
            setStoresStatus("idle")
            setStores(null)
            setStoresError("")
            return
        }
        setStoresStatus("loading")
        setStores(null)
        setStoresError("")

        getLoyaltyStores({ from: filters.period.from, to: filters.period.to })
            .then((response) => {
                if (storesRequestId.current !== requestId) return
                setStores(response.data)
                setStoresStatus("loaded")
            })
            .catch((error) => {
                if (storesRequestId.current !== requestId) return
                setStoresStatus("error")
                setStoresError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [filters.period.from, filters.period.to, isPeriodTooLong])

    const fetchLevels = useCallback(() => {
        const requestId = ++levelsRequestId.current
        if (isPeriodTooLong) {
            setLevelsStatus("idle")
            setLevelBreakdown(null)
            setLevelsError("")
            return
        }
        setLevelsStatus("loading")
        setLevelBreakdown(null)
        setLevelsError("")

        getLoyaltyLevelBreakdown({ from: filters.period.from, to: filters.period.to })
            .then((response) => {
                if (levelsRequestId.current !== requestId) return
                setLevelBreakdown(response.data)
                setLevelsStatus("loaded")
            })
            .catch((error) => {
                if (levelsRequestId.current !== requestId) return
                setLevelsStatus("error")
                setLevelsError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [filters.period.from, filters.period.to, isPeriodTooLong])

    const fetchBoundary = useCallback(() => {
        const requestId = ++boundaryRequestId.current
        if (isPeriodTooLong) {
            setBoundaryStatus("idle")
            setBoundarySummary(null)
            setBoundaryError("")
            return
        }
        setBoundaryStatus("loading")
        setBoundarySummary(null)
        setBoundaryError("")

        getLoyaltySummary(commonQuery)
            .then((response) => {
                if (boundaryRequestId.current !== requestId) return
                setBoundarySummary(response.data)
                setBoundaryStatus("loaded")
            })
            .catch((error) => {
                if (boundaryRequestId.current !== requestId) return
                setBoundaryStatus("error")
                setBoundaryError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [commonQuery, isPeriodTooLong])

    useEffect(() => fetchRows(), [fetchRows])
    useEffect(() => fetchStores(), [fetchStores])
    useEffect(() => fetchLevels(), [fetchLevels])
    useEffect(() => fetchBoundary(), [fetchBoundary])

    const storeNames = useMemo(() => {
        const names = new Map<string, string>()

        stores?.stores.forEach((store) => {
            const storeName = getStoreName(store.id, store.displayValue)
            if (storeName) names.set(store.id, storeName)
        })

        Object.values(partnerStores).forEach((storesGroup) => {
            storesGroup.forEach((store) => {
                const storeName = getStoreName(store.id, store.name, store.address)
                if (storeName) names.set(store.id, storeName)
            })
        })

        return names
    }, [partnerStores, stores])

    const storeOptions = useMemo(() => {
        const loadedIds = new Set(stores?.storeIds ?? [])
        return [
            { value: ALL_OPTION_VALUE, label: "Все точки" },
            ...(stores?.storeIds.map((storeId) => ({
                value: storeId,
                label: getStoreOptionLabel(storeId, storeNames.get(storeId)),
            })) ?? []),
            ...filters.storeIds
                .filter((storeId) => !loadedIds.has(storeId))
                .map((storeId) => ({
                    value: storeId,
                    label: getStoreOptionLabel(storeId, storeNames.get(storeId)),
                })),
            ...(stores?.hasUnassignedStore || filters.includeUnassignedStore
                ? [{
                    value: UNASSIGNED_OPTION_VALUE,
                    label: "Точка не указана",
                }]
                : []),
        ]
    }, [filters.includeUnassignedStore, filters.storeIds, storeNames, stores])

    const selectedStoreOptions = storeOptions.filter((option) =>
        (option.value !== ALL_OPTION_VALUE && filters.storeIds.includes(option.value))
        || (option.value === UNASSIGNED_OPTION_VALUE && filters.includeUnassignedStore)
    )

    const levelOptions = useMemo(() => {
        const rows = levelBreakdown?.rows ?? []
        const loadedLevels = new Set(rows.map((row) => String(row.level)))
        return [
            { value: ALL_OPTION_VALUE, label: "Все уровни" },
            ...rows.map((row) => ({
                value: String(row.level),
                label: `${row.level} — ${row.levelName}${row.isDeletedLevel ? " (удалён)" : ""}`,
            })),
            ...filters.levels
                .filter((level) => !loadedLevels.has(String(level)))
                .map((level) => ({ value: String(level), label: `Уровень ${level}` })),
            ...(Number(levelBreakdown?.unknownLevelAccrualsCount ?? 0) > 0 || filters.includeUnknownLevel
                ? [{ value: UNKNOWN_LEVEL_OPTION_VALUE, label: "Уровень не указан (н/д)" }]
                : []),
        ]
    }, [filters.includeUnknownLevel, filters.levels, levelBreakdown])

    const selectedLevelOptions = levelOptions.filter((option) =>
        (option.value === UNKNOWN_LEVEL_OPTION_VALUE && filters.includeUnknownLevel)
        || (
            option.value !== ALL_OPTION_VALUE
            && option.value !== UNKNOWN_LEVEL_OPTION_VALUE
            && filters.levels.includes(Number(option.value))
        )
    )

    const copyStoreDetails = useCallback((storeName: string | undefined, storeId: string) => {
        if (!navigator.clipboard) {
            notify.error("Копирование в буфер обмена недоступно")
            return
        }

        const clipboardText = storeName
            ? `Название: ${storeName}\nID: ${storeId}`
            : `ID торговой точки: ${storeId}`

        navigator.clipboard.writeText(clipboardText)
            .then(() => notify.success(
                storeName
                    ? "Название и ID торговой точки скопированы"
                    : "ID торговой точки скопирован"
            ))
            .catch(() => notify.error("Не удалось скопировать данные торговой точки"))
    }, [])

    const columns: ColumnDef<ILoyaltyAccrualRow, any>[] = useMemo(() => [
        {
            accessorFn: (row) => formatLoyaltyDateTime(row.createdAt),
            id: "createdAt",
            header: () => <div className="text-nowrap">Дата</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
        {
            accessorKey: "clientId",
            header: () => <div className="text-nowrap">Клиент</div>,
            enableSorting: false,
        },
        {
            accessorKey: "storeId",
            id: "store",
            header: () => <div className="text-nowrap">Точка</div>,
            cell: (info) => {
                const storeId = info.row.original.storeId
                if (!storeId) return "точка не указана"

                const storeName = storeNames.get(storeId)
                const storeLabel = storeName || UNKNOWN_STORE_LABEL
                return (
                    <div className="flex items-center gap-2">
                        <Link
                            to={`/partner/transactions?storeId=${encodeURIComponent(storeId)}`}
                            className="font-semibold text-color-primary hover:underline"
                            title={`Показать транзакции точки «${storeLabel}»`}
                        >
                            {storeLabel}
                        </Link>
                        <ActionButton
                            onClick={() => copyStoreDetails(storeName, storeId)}
                            icon={ClipboardTextSvg}
                            title="Скопировать название и ID торговой точки"
                        />
                    </div>
                )
            },
            enableSorting: false,
        },
        {
            accessorFn: (row) => row.loyaltyLevel == null ? "н/д" : row.loyaltyLevel,
            id: "loyaltyLevel",
            header: () => <div className="text-nowrap">Уровень</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
        {
            accessorFn: (row) => `${formatLoyaltyNumber(row.cashbackPercent)} %`,
            id: "cashbackPercent",
            header: () => <div className="text-nowrap">Кэшбэк</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
        {
            accessorFn: (row) => formatLoyaltyMoney(row.cashbackAmount),
            id: "cashbackAmount",
            header: () => <div className="text-nowrap">Начислено</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
        {
            accessorFn: (row) => formatLoyaltyMoney(row.paidAmountBonusDelta),
            id: "paidAmountBonusDelta",
            header: () => <div className="text-nowrap">Оплачено бонусами</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
        {
            accessorFn: (row) => formatLoyaltyMoney(row.paidAmountFiatDelta),
            id: "paidAmountFiatDelta",
            header: () => <div className="text-nowrap">Оплачено фиатом</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
        {
            accessorFn: (row) => row.isReturn ? "Да" : "Нет",
            id: "isReturn",
            header: () => <div className="text-nowrap">Возврат</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
        {
            accessorFn: (row) => row.isCashbackCredited ? "Да" : "Нет",
            id: "isCashbackCredited",
            header: () => <div className="text-nowrap">Зачислен</div>,
            cell: (info) => info.getValue(),
            enableSorting: false,
        },
    ], [copyStoreDetails, storeNames])

    const hasHistoricalNulls = accruals?.rows.some((row) =>
        row.createdAt === null
        || row.loyaltyLevel == null
        || row.paidAmountBonusDelta == null
        || row.paidAmountFiatDelta == null
    ) ?? false
    const totalPages = accruals ? Math.max(1, Math.ceil(Number(accruals.totalCount) / accruals.pageSize)) : 1

    return (
        <div className="_page_container">
            <PageHeader title="Начисления кэшбэка" />
            <div className="flexbox">
                <div className="box">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-lg font-bold screen:text-2xl">Период и фильтры</h2>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-[#5F666C]">Применено фильтров: {appliedFiltersCount}</span>
                            <button
                                type="button"
                                onClick={() => dispatch(resetFilters())}
                                className="form_cancel_button !h-10 !w-auto px-5"
                            >
                                Сбросить
                            </button>
                        </div>
                    </div>
                    <PeriodPicker
                        value={filters.period}
                        onChange={(period) => dispatch(setPeriod(period))}
                        withInterval={false}
                    />
                    {isPeriodTooLong && (
                        <div className="mt-3 text-color-danger">
                            Максимальный период — {MAX_PERIOD_DAYS} дней. Сузьте диапазон.
                        </div>
                    )}
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Select
                            isMulti
                            isLoading={storesStatus === "loading"}
                            options={storeOptions}
                            value={selectedStoreOptions}
                            placeholder="Все точки"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                if (values.length === 0 || values.includes(ALL_OPTION_VALUE)) {
                                    dispatch(setStoreIds([]))
                                    dispatch(setIncludeUnassignedStore(false))
                                    return
                                }
                                dispatch(setStoreIds(values.filter(
                                    (value: string) =>
                                        value !== ALL_OPTION_VALUE && value !== UNASSIGNED_OPTION_VALUE
                                )))
                                dispatch(setIncludeUnassignedStore(values.includes(UNASSIGNED_OPTION_VALUE)))
                            }}
                        />
                        <Select
                            isMulti
                            isLoading={levelsStatus === "loading"}
                            options={levelOptions}
                            value={selectedLevelOptions}
                            placeholder="Все уровни"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                if (values.length === 0 || values.includes(ALL_OPTION_VALUE)) {
                                    dispatch(setLevels([]))
                                    dispatch(setIncludeUnknownLevel(false))
                                    return
                                }
                                dispatch(setLevels(values
                                    .filter((value: string) => value !== UNKNOWN_LEVEL_OPTION_VALUE)
                                    .map(Number)))
                                dispatch(setIncludeUnknownLevel(values.includes(UNKNOWN_LEVEL_OPTION_VALUE)))
                            }}
                        />
                    </div>
                    <label className="mt-4 flex cursor-pointer items-center gap-3">
                        <input
                            type="checkbox"
                            checked={filters.includeReturns}
                            onChange={(event) => dispatch(setIncludeReturns(event.target.checked))}
                            className="h-5 w-5"
                        />
                        <span>Включая возвраты</span>
                    </label>
                    {storesStatus === "error" && (
                        <div className="mt-3"><RetryState message={storesError} onRetry={fetchStores} /></div>
                    )}
                    {storesStatus === "loaded" && stores?.storeIds.length === 0 && !stores.hasUnassignedStore && (
                        <div className="mt-3 text-sm text-[#828282]">нет данных о точках за период</div>
                    )}
                    {stores?.truncated && (
                        <div className="mt-3 rounded-xl bg-[#FFF4D6] p-3 text-[#8A5B00]">
                            Справочник точек усечён серверным лимитом
                        </div>
                    )}
                    {levelsStatus === "error" && (
                        <div className="mt-3"><RetryState message={levelsError} onRetry={fetchLevels} /></div>
                    )}
                    {levelsStatus === "loaded" && levelBreakdown?.rows.length === 0 && (
                        <div className="mt-3 text-sm text-[#828282]">нет активных уровней за период</div>
                    )}
                    {levelBreakdown?.truncated && (
                        <div className="mt-3 rounded-xl bg-[#FFF4D6] p-3 text-[#8A5B00]">
                            Список уровней усечён серверным лимитом
                        </div>
                    )}
                </div>

                {rowsStatus === "loading" && <div className="info_panel flex justify-center"><Loader /></div>}
                {rowsStatus === "error" && <RetryState message={rowsError} onRetry={fetchRows} />}
                {rowsStatus === "loaded" && accruals?.truncated && (
                    <div className="rounded-xl bg-[#FFF4D6] p-4 text-[#8A5B00]">
                        Результат усечён серверным лимитом. Уточните фильтры, чтобы увидеть все строки
                    </div>
                )}
                {rowsStatus === "loaded" && Number(accruals?.totalCount) === 0 && (
                    <NoTableData label="нет данных за период" />
                )}
                {rowsStatus === "loaded" && accruals && Number(accruals.totalCount) > 0 && (
                    <>
                        <div className="flex justify-end text-sm text-[#5F666C]">
                            Найдено строк: <span className="ml-1 font-bold">{formatLoyaltyNumber(accruals.totalCount)}</span>
                        </div>
                        <div className="table_box">
                            <Table columns={columns} data={accruals.rows} />
                        </div>
                        {hasHistoricalNulls && boundaryStatus === "loaded" && (
                            <div className="text-sm text-[#5F666C]">
                                {boundarySummary?.extendedFieldsSince == null
                                    ? "Раздельный учёт фиат/бонусов/уровня недоступен за выбранный период"
                                    : <>н/д — данные доступны с {
                                        formatExtendedFieldsSince(boundarySummary.extendedFieldsSince)
                                    }</>}
                            </div>
                        )}
                        {hasHistoricalNulls && boundaryStatus === "loading" && (
                            <div className="text-sm text-[#5F666C]">Загрузка даты доступности исторических данных…</div>
                        )}
                        {hasHistoricalNulls && boundaryStatus === "error" && (
                            <div className="info_panel flex items-center justify-between gap-4 text-sm">
                                <span className="text-color-danger">{boundaryError}</span>
                                <button
                                    type="button"
                                    onClick={fetchBoundary}
                                    className="form_handle_button !w-auto px-6"
                                >
                                    Повторить
                                </button>
                            </div>
                        )}
                        <Pagination
                            page={accruals.page}
                            totalPages={totalPages}
                            totalCount={Number(accruals.totalCount)}
                            setPage={(page) => dispatch(setPage(page))}
                        />
                    </>
                )}
            </div>
        </div>
    )
}
